import { useMemo } from "react";
import {
	useFilters,
	usePagination,
	useRowSelect,
	useSortBy,
} from "react-table";

import { DefaultColumnFilter } from "../components/filters/defaultColumnFilter";
import { IndeterminateCheckbox } from "../components/indeterminateCheckbox";
import { createFilterTypes } from "../config/filterTypes";
import { createSelectionStateReducer } from "../utils/selectionHelpers";

export const useTableConfiguration = ({
	columns,
	data,
	isFilter,
	isSelect,
	initPageSize = 20,
	initPageIndex = 0,
	sortees,
	shiftOn,
	ctrlOn,
	isServerSide = false,
}) => {
	const filterTypes = useMemo(() => createFilterTypes(), []);

	// TODO make normal sorting case insensitive
	const defaultColumn = useMemo(
		() => ({
			Filter: DefaultColumnFilter,
		}),
		[],
	);

	let useTableSettings = [
		{
			columns,
			data,
			autoResetSortBy: false,
			manualSortBy: isServerSide,
			initialState: {
				pageSize: initPageSize,
				pageIndex: initPageIndex,
				sortBy: sortees,
			},
		},
		useSortBy,
		...(~isServerSide ? [usePagination] : []),
	];

	if (isFilter) {
		useTableSettings = [
			{
				columns,
				data,
				defaultColumn,
				filterTypes,
				manualSortBy: isServerSide,
				initialState: {
					pageIndex: initPageIndex,
					pageSize: initPageSize,
					sortBy: sortees,
				},
				autoResetFilters: false,
				autoResetSortBy: false,
				previousRow: null,
				stateReducer: createSelectionStateReducer(shiftOn, ctrlOn),
			},
			useFilters,
			useSortBy,
			...(~isServerSide ? [usePagination] : []),
		];
	}

	if (isSelect) {
		useTableSettings = [
			...useTableSettings,
			useRowSelect,
			(hooks) => {
				hooks.visibleColumns.push((columns) => [
					{
						id: "selection",
						Header: ({
							toggleAllRowsSelected,
							getToggleAllRowsSelectedProps,
							isAllRowsSelected,
							selectedFlatRows,
						}) => (
							<IndeterminateCheckbox
								{...getToggleAllRowsSelectedProps()}
								toggleAllRowsSelected={toggleAllRowsSelected}
								indeterminate={
									!isAllRowsSelected &&
									selectedFlatRows.length > 0
								}
							/>
						),
						Cell: ({ row }) => (
							<IndeterminateCheckbox
								toggleRowSelected={() =>
									row.toggleRowSelected({
										isSelected: !row.isSelected,
										method: "checkbox",
									})
								}
								{...row.getToggleRowSelectedProps()}
							/>
						),
					},
					...columns,
				]);
			},
		];
	}

	return useTableSettings;
};
